import { wsUrl } from './ws-url.js';

export type WsConnectionStatus = 'connecting' | 'open' | 'closed' | 'failed';

export type ReconnectingWebSocketOptions = {
  path: string;
  onOpen?: () => void;
  onMessage?: (event: MessageEvent) => void;
  onStatus?: (status: WsConnectionStatus) => void;
  /** Stop after this many consecutive failures. Default 10. */
  maxFailures?: number;
};

/**
 * WebSocket with exponential reconnect backoff:
 * 1s → 2s → 4s → 8s … capped at 30s.
 * Resets on successful open; stops after maxFailures and reports `failed`.
 */
export function createReconnectingWebSocket(options: ReconnectingWebSocketOptions): {
  close: () => void;
} {
  const maxFailures = options.maxFailures ?? 10;
  let ws: WebSocket | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;
  let failures = 0;
  let stopped = false;

  function clearTimer() {
    if (reconnectTimer !== undefined) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
  }

  function backoffMs(): number {
    // attempt 0 → 1s, 1 → 2s, 2 → 4s, … max 30s
    return Math.min(30_000, 1000 * 2 ** attempt);
  }

  function scheduleReconnect() {
    if (stopped) return;
    failures += 1;
    if (failures >= maxFailures) {
      options.onStatus?.('failed');
      return;
    }
    const wait = backoffMs();
    attempt += 1;
    options.onStatus?.('closed');
    clearTimer();
    reconnectTimer = window.setTimeout(connect, wait);
  }

  function connect() {
    if (stopped) return;
    clearTimer();
    options.onStatus?.('connecting');

    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onopen = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      ws = undefined;
    }

    try {
      ws = new WebSocket(wsUrl(options.path));
    } catch {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      if (stopped) {
        ws?.close();
        return;
      }
      attempt = 0;
      failures = 0;
      options.onStatus?.('open');
      options.onOpen?.();
    };

    ws.onmessage = (event) => {
      if (stopped) return;
      options.onMessage?.(event);
    };

    ws.onclose = () => {
      if (stopped) return;
      scheduleReconnect();
    };

    ws.onerror = () => {
      // Browsers fire onerror before onclose; let onclose drive reconnect.
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    };
  }

  connect();

  return {
    close() {
      stopped = true;
      clearTimer();
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.onopen = null;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
      ws = undefined;
    },
  };
}
